import { useState, useEffect, useCallback } from "react";
import logger from "../utils/logger";

export interface UseInternetConnectionResult {
  isOnline: boolean;
  isChecking: boolean;
  lastChecked: number | null;
  checkConnection: () => Promise<boolean>;
}

/**
 * Hook to monitor internet connectivity
 * Uses navigator.onLine as primary indicator and performs periodic checks
 */
export const useInternetConnection = (): UseInternetConnectionResult => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [lastChecked, setLastChecked] = useState<number | null>(null);

  // Function to check internet connectivity by attempting to fetch a small resource
  const checkConnection = useCallback(async (): Promise<boolean> => {
    setIsChecking(true);

    try {
      // Try to fetch a small resource with a timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      // Use backend URL if available, otherwise use a reliable fallback
      // const checkUrl = process.env.REACT_APP_BACKEND_URL
      //   ? `${process.env.REACT_APP_BACKEND_URL}/api/health`
      //   : 'https://httpbin.org/status/200';
      const checkUrl = "https://httpbin.org/status/200";
      await fetch(checkUrl, {
        method: "HEAD",
        mode: "no-cors", // Avoid CORS issues
        cache: "no-cache",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      setLastChecked(Date.now());
      setIsOnline(true);
      //logger.info("Internet connection check: Online");
      return true;
    } catch (error) {
      setLastChecked(Date.now());
      setIsOnline(false);
      //logger.warn("Internet connection check: Offline", error);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Initial connection check
  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  // Listen to online/offline events
  useEffect(() => {
    const handleOnline = () => {
      //logger.info("Browser online event detected");
      setIsOnline(true);
      // Perform a quick check to verify actual connectivity
      checkConnection();
    };

    const handleOffline = () => {
      //logger.warn("Browser offline event detected");
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [checkConnection]);

  // Periodic connection check (every 30 seconds when offline, every 2 minutes when online)
  useEffect(() => {
    if (!isOnline) {
      // Check more frequently when offline
      const interval = setInterval(() => {
        checkConnection();
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    } else {
      // Check less frequently when online
      const interval = setInterval(() => {
        checkConnection();
      }, 120000); // 2 minutes

      return () => clearInterval(interval);
    }
  }, [isOnline, checkConnection]);

  return {
    isOnline,
    isChecking,
    lastChecked,
    checkConnection,
  };
};
